import compact from 'lodash-es/compact'
import { useEffect, useMemo, useState } from 'react'
import { useObservable } from 'react-use'

import { QueryStatus } from '../../lib/hooks/types'
import { NostoPlacementId } from '../../types/nosto'
import { useNostoClient } from '../_components/chrome/scripts/NostoScript'

export const useFetchNostoPlacementData = (
  nostoPlacementId: NostoPlacementId,
  options?: {
    skip?: boolean
    ignoreFallback?: boolean
  }
) => {
  const skip = options?.skip ?? false
  const nostoClient = useNostoClient()
  const nostoRecommendationsByPlacementId = useObservable(nostoClient.recommendations$, {})
  const [timeoutExpired, setTimeoutExpired] = useState(false)

  const selectedNostoRecommendationCollection = nostoRecommendationsByPlacementId[nostoPlacementId]

  const [nostoRecommendedSkuCodes, nostoRecommendedDeprecatedProductHandles, nostoProductTypes] =
    useMemo<[string[], string[], (string | undefined)[]]>(() => {
      if (skip) {
        return [[], [], []] as const
      }

      if (
        !selectedNostoRecommendationCollection ||
        (options?.ignoreFallback === true &&
          selectedNostoRecommendationCollection.result_id !== nostoPlacementId)
      ) {
        return [[], [], []] as const
      }

      const products =
        // TODO: Fix this auto-generated suppression.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        selectedNostoRecommendationCollection?.products.map(p => {
          const sku = (p.skus ?? []).concat().sort((thisSku, thatSku) => {
            return thisSku.available && thatSku.available ? 0 : thisSku.available ? -1 : 1
          })[0]?.custom_fields.skuCode
          const handle = p.handle
          const productType = p.custom_fields?.productType
          return {
            sku,
            handle,
            productType,
          }
        }) ?? []

      return [
        compact(products.map(p => p.sku)),
        compact(products.map(p => p.handle)),
        products.map(p => p.productType),
      ] as const
    }, [skip, nostoPlacementId, options?.ignoreFallback, selectedNostoRecommendationCollection])

  const status = useMemo<QueryStatus>(() => {
    if (skip) return 'resolved'
    if (selectedNostoRecommendationCollection) return 'resolved'
    if (timeoutExpired) return 'rejected'
    return 'pending'
  }, [selectedNostoRecommendationCollection, skip, timeoutExpired])

  useEffect(() => {
    if (status !== 'pending') return

    const timer = setTimeout(() => {
      setTimeoutExpired(true)
    }, 3000)

    return () => clearTimeout(timer)
  }, [status])

  return {
    data: {
      title: skip ? undefined : selectedNostoRecommendationCollection?.title,
      skusForVariantGroups: nostoRecommendedSkuCodes,
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      productCategoryId: nostoProductTypes?.[0],
      handlesForDeprecatedProducts: nostoRecommendedDeprecatedProductHandles,
      resultId: skip ? undefined : selectedNostoRecommendationCollection?.result_id,
    },
    status,
  }
}
